import { useEffect, useState, useCallback, useMemo } from 'react';
import { getUserRole, is_logged_in, get_user } from './functions';

let permissionsCache = new Map(); // Cache for permission checks

export const usePermissions = () => {
  const [userPermissions, setUserPermissions] = useState([]);
  const [userRole, setUserRole] = useState({});
  const [updateCounter, setUpdateCounter] = useState(0);

  // Memoize the current user to prevent unnecessary re-renders
  const currentUser = useMemo(() => get_user(), []);

  // Memoize permissions map for faster lookups
  const permissionsMap = useMemo(() => {
    const map = new Map();
    userPermissions.forEach((permission) => map.set(permission, true));
    return map;
  }, [userPermissions]);

  const fetchLatestPermissions = useCallback(async () => {
    try {
      if (is_logged_in()) {
        const res = await getUserRole();
        if (res?.status === 200) {
          const userData = res?.data;
          const userRole = userData?.role;
          const permissions = userRole?.permissions || [];

          setUserPermissions(permissions);
          setUserRole(userRole);
          setUpdateCounter((prev) => prev + 1);

          // Update cache
          permissionsCache.clear();
        }
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
    }
  }, []);

  // Optimized permission check with caching
  const hasPermission = useCallback(
    (permission) => {
      // Check cache first
      const cacheKey = `${userRole?.name}-${permission}`;
      if (permissionsCache.has(cacheKey)) {
        return permissionsCache.get(cacheKey);
      }

      // Super Admin bypass
      if (currentUser?.userType === 1 || userRole?.name === 'Super Admin') {
        permissionsCache.set(cacheKey, true);
        return true;
      }

      // Use Map for O(1) lookup instead of array includes
      const result = permissionsMap.has(permission);
      permissionsCache.set(cacheKey, result);
      return result;
    },
    [userRole?.name, permissionsMap, currentUser?.userType]
  );

  useEffect(() => {
    fetchLatestPermissions();

    // Listen for Firebase PERMISSIONS_UPDATED notifications (topic = userName)
    const handlePermissionsUpdate = (event) => {
      const { roleName, permissions } = event?.detail || {};
      if (currentUser?.role === roleName && Array.isArray(permissions)) {
        setUserPermissions(permissions);
        setUserRole((prev) => ({
          ...prev,
          name: roleName || prev.name,
          permissions
        }));
        setUpdateCounter((prev) => prev + 1);
        permissionsCache.clear();
      }
    };

    window.addEventListener('permissionsUpdated', handlePermissionsUpdate);

    return () => {
      window.removeEventListener('permissionsUpdated', handlePermissionsUpdate);
    };
  }, [currentUser?.role, fetchLatestPermissions]);

  return {
    hasPermission,
    userPermissions,
    refreshPermissions: fetchLatestPermissions,
    userRole,
    updateCounter
  };
};
